import { useRef, useState } from "react";
import classnames from "classnames";
import { t } from "@/i18n-js/instance";
import type {
  CreateAttemptFormattedPayload,
  CreateAttemptPayload,
} from "@/react/api/quizAttemptsApi";
import { useTailwindSmScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import type { CoursesLesson } from "@/react/types/CoursesLesson";
import type { QuizAttempt } from "@/react/types/QuizAttempt";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { TipTapBlockEditor } from "@circle-react-uikit/TipTapBlockEditor";
import { Typography } from "@circle-react-uikit/Typography";
import { QuizAttemptForm } from "./QuizAttemptForm";
import { QuizResult } from "./QuizResult";
import { QuizResultSkeleton } from "./QuizResultSkeleton";
import { useCreateQuizAttempt } from "./hooks/useCreateQuizAttempt";

const formatPayload = (
  payload: CreateAttemptPayload,
): CreateAttemptFormattedPayload => ({
  responses_attributes: payload.responses.map(response => ({
    ...response,
    selected_options_attributes: response.selected_options.filter(
      selectedOption => selectedOption.quizzes_option_id !== undefined,
    ),
  })),
});

interface QuizContentProps {
  isLessonPreview: boolean;
  isLoadingLastAttempt: boolean;
  lastAttempt: QuizAttempt | null;
  lesson: CoursesLesson;
  refetchLesson: () => void;
  setLastAttempt: (attempt: QuizAttempt | null) => void;
}
export const QuizContent = ({
  lesson,
  isLessonPreview,
  refetchLesson,
  lastAttempt,
  setLastAttempt,
  isLoadingLastAttempt,
}: QuizContentProps) => {
  const quiz = lesson.quiz;
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [onSuccess, setOnSuccess] = useState<(() => void) | null>(null);
  const isMobileScreen = !useTailwindSmScreenMediaQuery();
  const quizPanelRef = useRef<HTMLDivElement | null>(null);
  const { mutate, isSaving } = useCreateQuizAttempt({
    lesson,
    setLastAttempt,
    refetchLesson,
    quizPanelRef,
    onSuccess,
  });

  if (!quiz) return null;

  const description = lastAttempt ? lastAttempt.description : quiz.description;

  return (
    <div className="border-primary flex flex-col rounded-lg border">
      <div
        ref={quizPanelRef}
        className={classnames(
          "flex max-h-[calc(100dvh-356px)] flex-col gap-8 overflow-auto p-8",
          {
            // also substract the lesson preview banner if isLessonPreview: 48px in desktop, 64px in mobile
            "!max-h-[calc(100dvh-356px-64px)] sm:!max-h-[calc(100dvh-356px-48px)]":
              isLessonPreview,
            // also cover the space of the submit quiz section if lastAttempt exists
            "!max-h-[calc(100dvh-356px-64px+59px)] sm:!max-h-[calc(100dvh-356px-48px+59px)]":
              isLessonPreview && !!lastAttempt,
            "!max-h-[calc(100dvh-356px+59px)] sm:!max-h-[calc(100dvh-356px+59px)]":
              !!lastAttempt,
            "opacity-20": isSaving,
          },
        )}
      >
        {lastAttempt && !isLoadingLastAttempt && (
          <QuizResult
            lastAttempt={lastAttempt}
            setLastAttempt={setLastAttempt}
            setIsSubmitEnabled={setIsSubmitEnabled}
          />
        )}
        {isLoadingLastAttempt && <QuizResultSkeleton />}
        {description && (
          <div>
            <TipTapBlockEditor
              readOnly
              value={description.body}
              sgidToObjectMap={description.sgids_to_object_map}
              inlineAttachments={description.inline_attachments}
            />
          </div>
        )}
        <Form
          id={`quiz-${quiz.id}-form`}
          onSubmit={(payload: CreateAttemptPayload) =>
            mutate(formatPayload(payload))
          }
        >
          <QuizAttemptForm
            quiz={quiz}
            lastAttempt={lastAttempt}
            setIsSubmitEnabled={setIsSubmitEnabled}
            setOnSuccess={setOnSuccess}
          />
        </Form>
      </div>
      {!lastAttempt && !isLoadingLastAttempt && (
        <div className="border-primary flex items-center justify-between border-t px-8 py-3">
          <Typography.LabelMd>
            {t(
              `courses.view_lesson.${
                isSaving
                  ? "submitting_quiz"
                  : isMobileScreen
                    ? "answer_all_questions"
                    : "answer_all_questions_to_submit"
              }`,
            )}
          </Typography.LabelMd>
          <Button
            type="submit"
            variant="circle"
            form={`quiz-${quiz.id}-form`}
            disabled={!isSubmitEnabled || isSaving}
          >
            {t("courses.view_lesson.submit")}
          </Button>
        </div>
      )}
    </div>
  );
};
